/**
 * ProductCatalogHeader - Cabeçalho do catálogo de produtos
 * Contém seletor de tabela de preço, busca, filtro e contador
 * 
 * Versão: 1.3.0
 * - Adicionado layout responsivo para mobile (tamanhos reduzidos, empilhamento)
 */
import React from 'react';
import { Search, Table2, Tag, Package, Filter } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCrmPriceTables } from '@/hooks/useCrmPriceTables';

// Props do componente
interface ProductCatalogHeaderProps {
  selectedPriceTableId: string | null;
  onPriceTableChange: (id: string | null) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  showOnlyTableProducts: boolean;
  onShowOnlyTableProductsChange: (show: boolean) => void;
  itemsCount: number;
  currency: string;
  /** ID do workspace para filtrar tabelas de preço */
  workspaceId?: string | null;
}

/**
 * Cabeçalho do catálogo com filtros e busca
 */
export function ProductCatalogHeader({
  selectedPriceTableId,
  onPriceTableChange,
  searchQuery,
  onSearchChange,
  showOnlyTableProducts,
  onShowOnlyTableProductsChange,
  itemsCount,
  workspaceId,
}: ProductCatalogHeaderProps) {
  // Buscar tabelas de preço disponíveis (filtradas por workspace se fornecido)
  const { priceTables, isLoading: isLoadingTables } = useCrmPriceTables({ workspaceId });

  // Filtrar apenas tabelas ativas
  const activeTables = priceTables.filter(table => table.is_active);

  return (
    <div className="flex flex-col gap-2 md:gap-3 p-2 md:p-3 bg-muted/30 rounded-lg border border-border/50">
      {/* Linha 1: Tabela de Preço, Filtro e Contador */}
      <div className="flex items-center justify-between gap-2 md:gap-3 flex-wrap">
        {/* Seletor de Tabela de Preço */}
        <div className="flex items-center gap-1.5 md:gap-2 flex-1 min-w-[160px] md:min-w-[200px]">
          <Table2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-muted-foreground shrink-0" />
          <Select
            value={selectedPriceTableId || 'default'}
            onValueChange={(value) => onPriceTableChange(value === 'default' ? null : value)}
            disabled={isLoadingTables}
          >
            <SelectTrigger className="w-full max-w-[200px] md:max-w-[240px] h-6 md:h-8 text-[11px] md:text-xs">
              <SelectValue placeholder="Selecione tabela de preço" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">
                <div className="flex items-center gap-2">
                  <Tag className="h-3 w-3" />
                  <span>Preço Padrão</span>
                </div>
              </SelectItem>
              {activeTables.map((table) => (
                <SelectItem key={table.id} value={table.id}>
                  <div className="flex items-center gap-2">
                    <Table2 className="h-3 w-3" />
                    <span>{table.name}</span>
                    {table.is_default && (
                      <Badge variant="secondary" className="text-[10px] px-1 py-0">
                        Padrão
                      </Badge>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Toggle filtrar apenas da tabela */}
        {selectedPriceTableId && (
          <div className="flex items-center gap-1.5 md:gap-2">
            <Checkbox
              id="showOnlyTableProducts"
              checked={showOnlyTableProducts}
              onCheckedChange={(checked) => onShowOnlyTableProductsChange(checked === true)}
              className="h-3 w-3 md:h-4 md:w-4"
            />
            <Label 
              htmlFor="showOnlyTableProducts" 
              className="text-[10px] md:text-xs text-muted-foreground cursor-pointer whitespace-nowrap"
            >
              <Filter className="h-3 w-3 inline mr-1" />
              <span className="hidden sm:inline">Apenas da tabela</span>
              <span className="sm:hidden">Tabela</span>
            </Label>
          </div>
        )}

        {/* Contador de Itens */}
        <Badge 
          variant={itemsCount > 0 ? 'default' : 'secondary'} 
          className="shrink-0 text-[10px] md:text-xs"
        >
          <Package className="h-3 w-3 mr-1" />
          {itemsCount} {itemsCount === 1 ? 'item' : 'itens'}
        </Badge>
      </div>

      {/* Linha 2: Busca */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 md:h-4 md:w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nome, SKU ou descrição..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-8 md:pl-9 h-6 md:h-8 text-xs md:text-sm"
        />
      </div>
    </div>
  );
}
